import React from 'react'
import {
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled from 'styled-components'
import { IMonthlyTrend } from '../../../types/statistics.types'
import useWindowWidth from '../../../utils/use.window.width'

const Wrapper = styled.div`
	margin-bottom: 70px;

  svg,
	path,
	g {
		outline: none !important;
		focus-visible: none;
	}
`

const Title = styled.h2`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 20px;
	line-height: 114%;
	text-align: justify;
	color: #000;
	margin: 0 0 15px;

	@media (max-width: 530px) {
		font-size: 15px;
		margin: 0 0 10px;
	}
`

const Subtitle = styled.p`
	font-family: 'e-Ukraine', sans-serif;
	font-weight: 400;
	font-size: 14px;
	line-height: 114%;
	text-align: justify;
	color: #8f8f8f;
	margin: 0 0 20px;

	@media (max-width: 530px) {
		font-size: 12px;
	}
`

const YLabel = styled.div`
	position: absolute;
	left: 0;
	top: 50%;
	transform: rotate(-90deg) translateX(-50%);
	font-size: 11px;
	color: #888;
	transform-origin: left center;
	white-space: nowrap;
`

const TooltipBox = styled.div`
	background: #fff;
	border: 1px solid #d4e8dc;
	border-radius: 6px;
	padding: 10px 14px;
	font-size: 13px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`

const TooltipTitle = styled.div`
	font-weight: 600;
	color: #1a1a1a;
	margin-bottom: 4px;
`

const TooltipRow = styled.div`
	display: flex;
	align-items: center;
	gap: 6px;
	color: #444;
`

const Dot = styled.span`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #1a7a4a;
	display: inline-block;
`

const CustomTooltip = ({ active, payload, label }: any) => {
	if (active && payload?.length) {
		return (
			<TooltipBox>
				<TooltipTitle>{label}</TooltipTitle>
				<TooltipRow>
					<Dot />
					Отримано звернень:{' '}
					<strong>{payload[0].value.toLocaleString('uk-UA')}</strong>
				</TooltipRow>
			</TooltipBox>
		)
	}
	return null
}

const CustomDot = (props: any) => {
	const { cx, cy, value, index, data } = props
	const max = Math.max(...data.map((d: any) => d.total))
	const min = Math.min(...data.map((d: any) => d.total))

	let color = '#f0c040'
	if (value === max) color = '#f0c040'
	if (value === min) color = '#1a7a4a'

	const isFirst = index === 0
	const isLast = index === data.length - 1
	if (!isFirst && !isLast && value !== max && value !== min) {
		return (
			<circle
				cx={cx}
				cy={cy}
				r={4}
				fill='#888'
				stroke='#fff'
				strokeWidth={1.5}
			/>
		)
	}

	return (
		<rect
			x={cx - 5}
			y={cy - 5}
			width={10}
			height={10}
			fill={color}
			stroke='#fff'
			strokeWidth={1.5}
		/>
	)
}

interface Props {
	data: IMonthlyTrend[]
}

export const MonthlyTrendChartComponent: React.FC<Props> = ({ data }) => {
	const chartData = data.map(d => ({
		label: `${d.month} ${d.year}`,
		total: d.total,
	}))

	const maxVal = Math.max(...chartData.map(d => d.total))
	const maxItem = chartData.find(d => d.total === maxVal)

	const windowWidth = useWindowWidth()
	const isMobile = windowWidth <= 768

	return (
		<Wrapper>
			<Title>Динаміка звернень за останні 12 місяців</Title>
			<Subtitle>прийнято звернень</Subtitle>

			<div style={{ position: 'relative' }}>
				<YLabel>Кількість</YLabel>
				<ResponsiveContainer width='100%' height={isMobile ? 260 : 360}>
					<LineChart
						data={chartData}
						margin={{
							top: 20,
							right: 30,
							left: isMobile ? 10 : 40,
							bottom: isMobile ? 10 : 20,
						}}
					>
						<CartesianGrid stroke='#f0f0f0' vertical={false} />
						<XAxis
							dataKey='label'
							tick={{ fontSize: isMobile ? 8 : 11, fill: '#888' }}
							axisLine={false}
							tickLine={false}
							interval={0}
							angle={isMobile ? -90 : -35}
							textAnchor='end'
							height={isMobile ? 80 : 60}
						/>
						<YAxis
							tick={{ fontSize: isMobile ? 9 : 11, fill: '#888' }}
							axisLine={false}
							tickLine={false}
							domain={['auto', 'auto']}
							width={isMobile ? 45 : 60}
						/>
						{maxItem && (
							<ReferenceLine
								x={maxItem.label}
								stroke='#e8f0f8'
								strokeWidth={28}
								label=''
							/>
						)}
						<Tooltip content={<CustomTooltip />} />
						<Line
							type='monotone'
							dataKey='total'
							stroke='#b0b8c0'
							strokeWidth={2}
							dot={props => <CustomDot {...props} data={chartData} />}
							activeDot={{
								r: 6,
								fill: '#1a7a4a',
								stroke: '#fff',
								strokeWidth: 2,
							}}
							label={
								isMobile
									? false
									: ({ x, y, value }) => {
										
											if (typeof y !== 'number') return null
											return (
												<text
													x={x}
													y={y - 12}
													textAnchor='middle'
													fontSize={11}
													fill='#444'
													fontWeight={600}
												>
													{value?.toLocaleString('uk-UA')}
												</text>
											)
										}
							}
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		</Wrapper>
	)
}
